import { DataGrid, GridColDef } from "@mui/x-data-grid";
import { esES, ptBR } from '@mui/material/locale';
import { IReuniao, StatusReuniaoEnum, StatusValidacaoEnum } from "../../commons/types/crm";
import moment from "moment";
import { 
  Paper, 
  ThemeProvider, 
  Typography, 
  Box, 
  createTheme, 
  useTheme,
  Skeleton
} from "@mui/material";
import { AcoesColumn } from "./acoesColumn";
import { useState } from "react";
import DrawerConcluir, { IDrawerConcluir } from "./Components/DrawerConcluir";
import { useRecoilValue } from "recoil";
import { authAtom } from "../../states/usuarios/AuthState";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { useTranslation } from "../../commons/useTranslasion";
import { themeConstants } from "../../commons/theme";

dayjs.extend(utc);
dayjs.extend(timezone);

interface ITableReunioes {
  reunioes: IReuniao[];
  loading: boolean;
}

export const handleStatus = (
  status: StatusReuniaoEnum,
  statusValidacao: StatusValidacaoEnum | undefined,
  translate: Function
) => {
  if (statusValidacao !== undefined && statusValidacao !== StatusValidacaoEnum.PENDENTE) {
    if (statusValidacao === StatusValidacaoEnum.APROVADO) {
      return translate('reuniaoAprovada');
    } else if (statusValidacao === StatusValidacaoEnum.REPROVADO) {
      return translate('reuniaoReprovada');
    }
  }

  const statusMap: Record<StatusReuniaoEnum, string | Record<StatusValidacaoEnum, string>> = {
    [StatusReuniaoEnum.PENDENTE]: translate('pendenteAgendamento'),
    [StatusReuniaoEnum.AGENDADA]: translate('agendada'),
    [StatusReuniaoEnum.PENDENTE_APROVACAO]: translate('aguardandoAprovacao'),
    [StatusReuniaoEnum.APROVADA]: {
      [StatusValidacaoEnum.APROVADO]: translate('reuniaoAprovada'),
      [StatusValidacaoEnum.REPROVADO]: translate('reuniaoReprovada'),
      [StatusValidacaoEnum.PENDENTE]: translate('aguardandoAprovacaoGestor')
    },
    [StatusReuniaoEnum.CANCELADA]: translate('cancelada'),
    [StatusReuniaoEnum.NO_SHOW]: translate('naoCompareceu')
  };

  const statusValue = statusMap[status];

  if (typeof statusValue === 'object' && statusValidacao !== undefined) {
    return statusValue[statusValidacao] || translate('aguardandoAprovacaoGestor');
  }

  return statusValue || '';
};

export const forcarTimezone = (data: any) => {
  return dayjs(data);
};

const TableReunioes: React.FC<ITableReunioes> = (props) => {
  const { reunioes, loading } = props;
  const [open, setOpen] = useState(false);
  const [loadingSave, setLoadingSave] = useState(false);
  const [reuniao, setReuniao] = useState<IReuniao>();
  const auth = useRecoilValue(authAtom);
  const { translate, language } = useTranslation();
  const lang = language ?? "pt-br";
  const theme = useTheme();
  const { colors, shape, spacing } = themeConstants;
  
  let locale = ptBR;
  switch (lang.slice(0, 2)) {
    case "es":
      locale = esES
      break;
  }

  // Configurações de locale para o DataGrid
  const dataGridTheme = createTheme({}, locale);

  const columns: (args: IDrawerConcluir) => GridColDef[] = (
    args: IDrawerConcluir,
  ) => {
    return [
      {
        field: "nomeAssessorado",
        headerName: translate("assessorado"),
        minWidth: 150,
        flex: 1,
      },
      {
        field: "nomeEtapa",
        headerName: translate("etapa"),
        minWidth: 120,
        flex: 0.8,
      },
      {
        field: "data",
        headerName: translate("data"),
        minWidth: 100,
        flex: 0.6,
        valueGetter(value: any, row: IReuniao) {
          const reuniao = row as IReuniao;
          if (
            reuniao.status === StatusReuniaoEnum.PENDENTE_APROVACAO ||
            reuniao.status === StatusReuniaoEnum.APROVADA
          )
            return moment(reuniao.dtRealizadaInicio).format("DD/MM");
          return moment(reuniao.dtPrevista).format("DD/MM");
        },
      },
      {
        field: "horaInicio",
        headerName: translate("inicio"),
        minWidth: 100,
        flex: 0.6,
        valueGetter(params: any, row: IReuniao) {
          const reuniao = row;
          return moment(forcarTimezone(reuniao.dtRealizadaInicio!.toString()).toDate().toString()).format('HH:mm');
        },
      },
      {
        field: "horaFim",
        headerName: translate("fim"),
        minWidth: 100,
        flex: 0.6,
        valueGetter(params: any, row: IReuniao) {
          const reuniao = row;
          return moment(forcarTimezone(reuniao.dtRealizadaFim!.toString()).toDate().toString()).format('HH:mm');
        },
      },
      {
        field: "status",
        headerName: translate("status"),
        minWidth: 140,
        flex: 1,
        valueGetter(params: any, row: IReuniao) {
          const reuniao = row;
          return handleStatus(reuniao.status, reuniao.statusValidacao, translate);
        },
      },
      {
        field: "",
        headerName: translate("acoes"),
        minWidth: 120,
        flex: 0.8,
        editable: false,
        sortable: false,
        filterable: false,
        renderCell: (params) => (
          <AcoesColumn
            row={params.row}
            setOpen={args.setOpen}
            setReuniao={args.setReuniao!}
          />
        ),
      },
    ];
  };

  const renderDataGrid = () => {
    if (loading) {
      return (
        <Box sx={{ mt: 3 }}>
          <Skeleton variant="rectangular" height={50} animation="wave" />
          <Skeleton variant="rectangular" height={400} sx={{ mt: 1 }} animation="wave" />
        </Box>
      );
    }

    return (
      <ThemeProvider theme={dataGridTheme}>
        <DataGrid
          rows={reunioes?.filter((r: IReuniao) => r.idConsultor == auth?.id && r.ativo) ?? []}
          loading={loading}
          columns={columns({
            open,
            setOpen,
            loadingSave,
            setLoadingSave,
            setReuniao,
          })}
          sx={{
            minHeight: "400px",
            marginTop: "20px",
            height: "auto",
            width: '100%',
            maxWidth: '100%',
            overflow: 'auto',
            border: 'none',
            borderRadius: `${themeConstants.shape.borderRadius.medium}px`,
            '& .MuiDataGrid-columnHeaders': {
              backgroundColor: themeConstants.colors.background.light,
              borderBottom: `1px solid ${themeConstants.colors.gray.light}`,
            },
            '& .MuiDataGrid-cell': {
              borderBottom: `1px solid ${themeConstants.colors.gray.light}`,
            },
            '& .MuiDataGrid-row:hover': {
              backgroundColor: `${themeConstants.colors.gray.light}80`, // 50% de opacidade
            },
            '& .MuiTablePagination-root': {
              color: themeConstants.colors.gray.main,
            },
          }}
          autoHeight
          initialState={{
            pagination: {
              paginationModel: { pageSize: 10, page: 0 },
            },
          }}
          pageSizeOptions={[5, 10, 25]}
          disableRowSelectionOnClick
          disableColumnMenu
        />
      </ThemeProvider>
    );
  };

  return (
    <>
      <Paper 
        sx={{ 
          padding: { xs: 2, sm: "30px" }, 
          borderRadius: "10px",
          width: '100%',
          overflow: 'hidden',
          boxSizing: 'border-box'
        }} 
        elevation={3}
      >
        <Typography variant="h3">{translate("reunioes")}</Typography>
        {renderDataGrid()}
      </Paper>

      {reuniao && (
        <DrawerConcluir
          reuniao={reuniao}
          open={open}
          setOpen={setOpen}
          loadingSave={loadingSave}
          setLoadingSave={setLoadingSave}
        />
      )}
    </>
  );
};

export default TableReunioes;
